import Spinner from './Spinner';

// Las clases del botón, también para los ENLACES que se ven como botón
// (WhatsApp, correo, /precios): un <a> no puede ser <button> y copiar las
// clases a mano las hacía divergir. Button las usa igual que antes.
const VARIANT_CLASSES = {
  // primary apunta a los tokens nuevos del paso 4a (--color-accent slate)
  // vía arbitrary values, no a bg-accent (que sigue mapeado al legacy
  // royal blue para no romper el JSX no migrado). En paso 4d, cuando se
  // unifique, esto vuelve a clases semánticas planas.
  primary:
    'bg-[var(--color-accent)] text-paper border-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] hover:border-[var(--color-accent-hover)]',
  secondary:
    'bg-paper text-ink border-edge hover:bg-surface',
  ghost:
    'bg-transparent text-ink-muted border-transparent hover:text-ink hover:border-edge',
  danger:
    'bg-system-error-bg text-system-error border-system-error-border hover:bg-system-error-bg/80',
};

const SIZE_CLASSES = {
  sm: 'px-3 py-1.5 text-small',
  md: 'px-4 py-2 text-small',
  lg: 'px-5 py-2.5 text-body font-semibold',
};

const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper';

export function clasesBoton({ variant = 'primary', size = 'md' } = {}) {
  return [
    'inline-flex items-center justify-center gap-2 font-sans font-semibold',
    'rounded-tk-md border transition-all duration-150',
    VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary,
    SIZE_CLASSES[size] || SIZE_CLASSES.md,
    FOCUS_RING,
  ].join(' ');
}

/**
 * Único componente de botón del producto. Reemplaza Btn, Button local,
 * BtnPrimary/BtnSecondary y todos los <button bg-accent...> inline.
 *
 * @param {Object} props
 * @param {'primary'|'secondary'|'ghost'|'danger'} [props.variant='primary']
 * @param {'sm'|'md'|'lg'}                          [props.size='md']
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.loading=false]    Reemplaza children por Spinner +
 *                                            loadingLabel.
 * @param {string}  [props.loadingLabel='Procesando…']
 * @param {React.ReactNode} [props.iconLeft]
 * @param {React.ReactNode} [props.iconRight]
 * @param {(e: React.MouseEvent) => void} [props.onClick]
 * @param {'button'|'submit'|'reset'} [props.type='button']
 * @param {string}  [props.className='']     Solo overrides excepcionales (ej. w-full).
 *                                            No para color.
 * @param {string}  [props.ariaLabel]        Nombre accesible cuando el texto visible no basta
 *                                            (varios "Copiar" en una lista, uno por paciente).
 * @param {React.ReactNode} props.children
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  loadingLabel = 'Procesando…',
  iconLeft,
  iconRight,
  onClick,
  type = 'button',
  className = '',
  ariaLabel,
  children,
}) {
  const isDisabled = disabled || loading;
  const cursorClass = loading
    ? 'cursor-wait'
    : disabled
    ? 'cursor-not-allowed opacity-50'
    : 'active:scale-[0.98]';

  // Tone del Spinner interno: en primary/danger va sobre fondos saturados → paper;
  // en secondary/ghost va sobre fondo claro → accent.
  const spinnerTone =
    variant === 'primary' || variant === 'danger' ? 'accent' : 'accent';
  // Mismo tono accent funciona en ambos contextos; si en QA visual queda mal
  // sobre primary, se ajusta.

  return (
    <button
      type={type}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      className={[
        clasesBoton({ variant, size }),
        cursorClass,
        className,
      ].join(' ')}
    >
      {loading ? (
        <>
          <Spinner tone={spinnerTone} size="sm" />
          <span>{loadingLabel}</span>
        </>
      ) : (
        <>
          {iconLeft}
          <span>{children}</span>
          {iconRight}
        </>
      )}
    </button>
  );
}
